import { ApplianceId, DiffuserId, GroupId, HoodId, RoomId, VentilatedCeilingId } from '@/api/airchef/Api';
import { generateRoomPartId, getCurrentTimestamp, simpleClone } from '@/common/utils';
import { Appliance, Diffuser, Group, Hood, VentilatedCeiling } from '@/types/helpers';
import { updateAssetModifiedPlain } from './helpers';
import { InvalidStateError } from '@/common/errors';

type ProjectAssetData = {
  appliances: Record<ApplianceId, Appliance>;
  hoods: Record<HoodId, Hood>;
  ceilings: Record<VentilatedCeilingId, VentilatedCeiling>;
  diffusers: Record<DiffuserId, Diffuser>;
  groups: Record<GroupId, Group>;
};

const IdMap = Map as new (
  entries?:
    | [RoomId, RoomId]
    | [HoodId, HoodId]
    | [ApplianceId, ApplianceId]
    | [DiffuserId, DiffuserId]
    | [GroupId, GroupId]
    | [VentilatedCeilingId, VentilatedCeilingId],
) => Map<RoomId, RoomId> &
  Map<HoodId, HoodId> &
  Map<ApplianceId, ApplianceId> &
  Map<DiffuserId, DiffuserId> &
  Map<GroupId, GroupId> &
  Map<VentilatedCeilingId, VentilatedCeilingId>;

export function duplicateRoomAssets(
  sourceRoomId: RoomId,
  targetRoomId: RoomId,
  user: string,
  data: Readonly<ProjectAssetData>,
): Partial<ProjectAssetData> {
  const now = getCurrentTimestamp();
  const idMap = new IdMap();

  const ret: ProjectAssetData = {
    appliances: {},
    hoods: {},
    ceilings: {},
    diffusers: {},
    groups: {},
  };

  // First clone hoods, and update them to the new room
  for (const h of Object.values(data.hoods).filter((h) => h.relations.roomId === sourceRoomId)) {
    const clone = simpleClone(h);

    updateAssetModifiedPlain(clone, now, user, true);
    clone.id = generateRoomPartId();
    clone.relations.roomId = targetRoomId;
    // Remove links to things outside the room
    clone.relations.exhaustId = undefined;
    clone.relations.zoneId = undefined;

    idMap.set(h.id, clone.id);
    ret.hoods[clone.id] = clone;
  }

  // Then, appliances
  for (const a of Object.values(data.appliances).filter((a) => a.relations.roomId === sourceRoomId)) {
    const clone = simpleClone(a);

    updateAssetModifiedPlain(clone, now, user, true);
    clone.id = generateRoomPartId();
    clone.relations.roomId = targetRoomId;

    idMap.set(a.id, clone.id);
    ret.appliances[clone.id] = clone;

    // Update appliance-hood links as well
    clone.relations.hoodIdsAbove = clone.relations.hoodIdsAbove?.map((hids) => {
      const hid = hids as HoodId;
      const chid = idMap.get(hid);
      if (!chid) throw new InvalidStateError('Could not find cloned hood to map for hoodIdsAbove');
      return chid;
    });
    clone.relations.hoodIds = clone.relations.hoodIds?.map((hid) => {
      const chid = idMap.get(hid);
      if (!chid) throw new InvalidStateError('Could not find cloned hood to map for hoodIds');
      return chid;
    });
  }

  // Clone ventilated ceilings
  for (const c of Object.values(data.ceilings).filter((c) => c.relations.roomId === sourceRoomId)) {
    const clone = simpleClone(c);

    updateAssetModifiedPlain(clone, now, user, true);
    clone.id = generateRoomPartId();
    clone.relations.roomId = targetRoomId;

    idMap.set(c.id, clone.id);
    ret.ceilings[clone.id] = clone;
  }

  // Clone diffusers
  for (const d of Object.values(data.diffusers).filter((d) => d.relations.roomId === sourceRoomId)) {
    const clone = simpleClone(d);

    updateAssetModifiedPlain(clone, now, user, true);
    clone.id = generateRoomPartId();
    clone.relations.roomId = targetRoomId;
    // Remove links to things outside the room
    clone.relations.supplyId = undefined;
    clone.relations.vavId = undefined;
    clone.relations.zoneId = undefined;

    idMap.set(d.id, clone.id);
    ret.diffusers[clone.id] = clone;
  }

  // Clone groups
  for (const g of Object.values(data.groups).filter((g) => g.relations.roomId === sourceRoomId)) {
    const clone = simpleClone(g);

    clone.id = generateRoomPartId();
    clone.relations.roomId = targetRoomId;

    idMap.set(g.id, clone.id);
    ret.groups[clone.id] = clone;

    // Update rows, these were cloned with the group. So need to update the row IDs and links to the group & rows
    // for any assets in the groups
    for (const row of clone.rows) {
      row.id = generateRoomPartId();
      // Update all appliances and hoods to point to the new row
      for (const oaid of row.relations.applianceIds) {
        const caid = idMap.get(oaid);
        if (!caid) throw new InvalidStateError('Could not find cloned appliance id while cloning room!');
        const a = ret.appliances[caid];
        if (!a) throw new InvalidStateError('Could not find cloned appliance while cloning room!');

        a.relations.groupId = clone.id;
        a.relations.rowId = row.id;
      }
      for (const ap of row.relations.floatingApplianceIds) {
        const caid = idMap.get(ap.applianceId);
        if (!caid) throw new InvalidStateError('Could not find cloned floating appliance id while cloning room!');
        const a = ret.appliances[caid];
        if (!a) throw new InvalidStateError('Could not find cloned floating appliance while cloning room!');

        a.relations.groupId = clone.id;
        a.relations.rowId = row.id;
      }
      for (const ohid of row.relations.hoodIds) {
        const chid = idMap.get(ohid);
        if (!chid) throw new InvalidStateError('Could not find cloned hood id while cloning room!');
        const a = ret.hoods[chid];
        if (!a) throw new InvalidStateError('Could not find cloned hood while cloning room!');

        a.relations.groupId = clone.id;
        a.relations.rowId = row.id;
      }

      // Update the row relations to use the new asset IDs
      row.relations.applianceIds = row.relations.applianceIds.map((aid) => {
        const caid = idMap.get(aid);
        if (!caid) throw new InvalidStateError('Could not find cloned appliance id while cloning room!');
        return caid;
      });
      row.relations.floatingApplianceIds = row.relations.floatingApplianceIds.map((ap) => {
        const caid = idMap.get(ap.applianceId);
        if (!caid) throw new InvalidStateError('Could not find cloned floating appliance id while cloning room!');
        return {
          ...ap,
          applianceId: caid,
        };
      });
      row.relations.hoodIds = row.relations.hoodIds.map((hid) => {
        const chid = idMap.get(hid);
        if (!chid) throw new InvalidStateError('Could not find cloned hood id while cloning room!');
        return chid;
      });
    }

    // Update groups for ceilings
    for (const c of Object.values(ret.ceilings)) {
      c.relations.groupIds = c.relations.groupIds?.map((gid) => {
        const cgid = idMap.get(gid);
        if (!cgid) throw new InvalidStateError('Could not find cloned group to update ceiling group links!');
        return cgid;
      });
    }

    // Update clone group relations
    clone.relations.applianceIds = clone.relations.applianceIds.map((aid) => {
      const caid = idMap.get(aid);
      if (!caid) throw new InvalidStateError('Could not find cloned appliance id while cloning room!');
      return caid;
    });
    clone.relations.hoodIds = clone.relations.hoodIds.map((hid) => {
      const chid = idMap.get(hid);
      if (!chid) throw new InvalidStateError('Could not find cloned hood id while cloning room!');
      return chid;
    });
  }

  return ret;
}
